/**
 * DevTypographyMenu — DEV-ONLY drop-up for experimenting with chat typography.
 *
 * Same pattern as DevToolRenderMenu (gated on import.meta.env.DEV, renders
 * null in prod). Selecting a preset applies a `.chat-type-*` class to the
 * chat-panel root; the md-chat prose preset reads the `--chat-*` variables
 * those classes override (see index.css "Chat typography presets"). The
 * choice persists in localStorage so it survives reloads while comparing.
 *
 * This is an EXPERIMENT HARNESS: once a winner is picked, fold its values
 * into the md-chat defaults (features/collab/tailwind/presets/chat.js) or a
 * real display option, and delete the losing presets.
 */
import { useState, useRef, useEffect } from 'react'
import { Type, ChevronUp, X } from 'lucide-react'

const STORAGE_KEY = 'papan:dev-chat-type'

export interface ChatTypePreset {
  /** Space-separated class list applied to the chat panel root ('' = default) */
  id: string
  label: string
  hint: string
}

export const CHAT_TYPE_PRESETS: ChatTypePreset[] = [
  // Default = the 2026-07-04 winner ("Relaxed + soft"), now baked into md-chat.
  { id: '', label: 'Default', hint: 'relaxed+soft · 14px · lh 1.8 · gap 1em' },
  { id: 'chat-type-compact', label: 'Compact (old)', hint: 'the pre-redesign control · lh 1.6 · gap 0.5em' },
  { id: 'chat-type-large', label: 'Large', hint: '15px body' },
  { id: 'chat-type-serif', label: 'Serif reading', hint: 'Charter/Georgia · 15px' },
  { id: 'chat-type-unsoft', label: 'Full contrast', hint: 'undo the 84% text blend' },
]

export function loadDevChatType(): string {
  if (!import.meta.env.DEV) return ''
  try {
    const v = localStorage.getItem(STORAGE_KEY) || ''
    // Ids from a previous experiment round (e.g. the graduated winner)
    // no longer exist — drop them instead of showing a dead "custom" chip.
    if (!CHAT_TYPE_PRESETS.some(p => p.id === v)) { localStorage.removeItem(STORAGE_KEY); return '' }
    return v
  } catch { return '' }
}

interface Props {
  value: string
  onChange: (classes: string) => void
}

/** DEV-ONLY. Renders null in production builds. */
export function DevTypographyMenu({ value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  if (!import.meta.env.DEV) return null

  const active = CHAT_TYPE_PRESETS.find(p => p.id === value)
  const isCustom = value !== '' && !active

  const select = (id: string) => {
    try { id ? localStorage.setItem(STORAGE_KEY, id) : localStorage.removeItem(STORAGE_KEY) } catch { /* private mode */ }
    onChange(id)
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title="Dev: chat typography experiments"
        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
          value
            ? 'text-brand-300 border-brand-500/50 bg-brand-500/10'
            : 'themed-text-muted hover:themed-text themed-bg-secondary themed-border'
        }`}
      >
        <Type size={11} />
        <span className="max-w-[90px] truncate">{active && active.id ? active.label : isCustom ? value : 'Type'}</span>
        <ChevronUp size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-64 rounded-lg themed-bg-secondary border themed-border shadow-lg z-50">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider themed-text-muted font-medium border-b themed-border">
            Chat typography (dev)
          </div>
          {value && (
            <button
              type="button"
              onClick={() => select('')}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 transition-colors border-b themed-border"
            >
              <X size={11} />
              <span className="flex-1 text-left">Reset to default</span>
            </button>
          )}
          {CHAT_TYPE_PRESETS.map(p => {
            const isActive = p.id === value
            return (
              <button
                key={p.id || 'default'}
                type="button"
                onClick={() => select(p.id)}
                className={`w-full flex flex-col items-start px-3 py-1.5 transition-colors ${
                  isActive ? 'themed-text bg-brand-500/10' : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'
                }`}
              >
                <span className="text-xs">{p.label}</span>
                <span className="text-[10px] opacity-60">{p.hint}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
